import Component from '@glimmer/component';
import { action, set } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask, task } from 'ember-concurrency';
import { isEmpty } from '@ember/utils';
import { pluralize } from 'ember-inflector';
import { tracked } from '@glimmer/tracking';
import { formatScimProvider } from 'garaje/utils/format-scim-provider';
import { formatDistanceToNow } from 'date-fns';

/**
 * @param {Object}              model
 * @param {String}              integration
 * @param {Function}            openAction
 * @param {Function}            closeAction
 * @param {Function}            integrationDisconnected
 * @param {Function}            save
 */
export default class ScimConfigBox extends Component {
  // TODO: file needs to be cleaned up after "scim-admins" flag is gone
  @service currentAdmin;
  @service store;
  @service flashMessages;
  @service featureFlags;

  @tracked showConfirmDisconnectDialog = false;
  @tracked showConfirmRegenerateDialog = false;

  progressImgPath = '/assets/images/orange-progress-small.svg';
  oneloginIcon = '/assets/images/integrations/onelogin-small.png';
  oktaIcon = '/assets/images/integrations/okta-small.png';
  azureIcon = '/assets/images/integrations/azure.svg';
  centrifyIcon = '/assets/images/integrations/centrify.svg';

  get connected() {
    return !isEmpty(this.args.model);
  }

  get isOpen() {
    return this.args.integration === 'scim';
  }

  get providerLogo() {
    return this[`${this.args.model.provider}Icon`];
  }

  get formattedProvider() {
    const { model } = this.args;
    return model && model.provider ? formatScimProvider(model.provider) : '';
  }

  get learnMoreLink() {
    switch (this.args.model.provider) {
      case 'onelogin':
        return 'https://envoy.help/en/articles/3453393-onelogin-application';
      case 'azure':
        return 'https://envoy.help/en/articles/3453335-microsoft-azure-active-directory-integration';
      default:
        // okta
        return 'https://envoy.help/en/articles/3453325-okta';
    }
  }

  get pillText() {
    const model = this.args.model;
    if (model) {
      const numOfLocations = model.locationMapping?.length || 0;
      return numOfLocations > 0 ? pluralize(numOfLocations, 'location') : 'All Users';
    } else {
      return '';
    }
  }

  get lastSyncedAt() {
    if (this.args.model?.lastSyncAt) {
      const formattedText = formatDistanceToNow(new Date(this.args.model.lastSyncAt), { addSuffix: true });
      return `Last synced ${formattedText}`;
    } else {
      return '';
    }
  }

  pushState(state) {
    this.args.model.store.push({
      data: {
        id: this.args.model.id,
        type: 'scim-integration',
        attributes: {
          state,
        },
      },
    });
  }

  confirmDisconnect() {
    return window.confirm('Are you sure you want to disconnect this integration?');
  }

  confirmRegenerate() {
    return window.confirm(
      "Are you sure you want regenerate the token? You'll need to configure your provider with this new token."
    );
  }

  @action
  close() {
    if (this.args.model?.isNew) {
      this.args.model.destroyRecord();
      this.args.integrationDisconnected();
    }

    this.args.closeAction();
  }

  @action
  open() {
    this.args.openAction('scim');
  }

  @action
  copySuccess() {
    this.flashMessages.showAndHideFlash('success', 'Copied');
  }

  @dropTask
  *changeStateTask() {
    try {
      if (this.args.model.isEnabled) {
        set(this.args.model, 'state', 'disabled');

        yield this.args.model.save();

        this.close();
      } else {
        set(this.args.model, 'state', 'enabled');

        yield this.args.model.save();
      }

      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', e);
    }
  }

  @dropTask
  *regenerateToken() {
    if (yield this.confirmRegenerate()) {
      yield this.args.model.regenerateToken().then((data) => {
        const { store } = this.args.model;
        return store.push(store.normalize('scim-integration', data.data));
      });
    }
  }

  isValid(model) {
    if (!this.args.model.locationMapping) {
      return true;
    }

    return !model.locationMapping.any(function (mapping) {
      return isEmpty(mapping.value);
    });
  }

  @dropTask
  *saveTask() {
    try {
      if (this.isValid(this.args.model)) {
        const model = yield this.args.model.save();

        this.flashMessages.showAndHideFlash('success', 'Saved!');
        if (this.featureFlags.isEnabled('scim-admins')) {
          this.args.closeAction();
        }

        return model;
      } else {
        return this.flashMessages.showFlash('error', 'Please add a value for every chosen filter.');
      }
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', e);
    }
  }

  @task
  *disconnectTask() {
    if (!this.confirmDisconnect()) {
      return;
    }
    yield this.args.model.destroyRecord();
    this.close();
    this.args.integrationDisconnected(true);
  }

  @action
  save(callback) {
    return callback(this.saveTask.perform());
  }
}
